import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Accordion,
  AccordionItem,
  AccordionItemHeader,
  AccordionItemBody,
} from '@razorpay/blade/components';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { BankAccountResponse } from 'api/employeeBankAccount';
import { getStatusBadgeProps } from './utils';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

export const getActivationPeriod = ({
  isActive,
  createdAt,
  deactivatedAt,
}: {
  isActive: boolean;
  createdAt: string | null;
  deactivatedAt: string | null;
}) => {
  if (!createdAt) {
    return '--';
  }
  let period = getFormattedDateValue({
    date: createdAt,
    formatString: dateFormats.shortDateMonthCommaShortYear,
  });
  if (isActive) {
    period = `${period} - Present`;
  }
  if (deactivatedAt) {
    period = `${period} - ${getFormattedDateValue({
      date: deactivatedAt,
      formatString: dateFormats.shortDateMonthCommaShortYear,
    })}`;
  }
  return period;
};

export const AccountHistory = ({ accountHistory }: { accountHistory: BankAccountResponse[] }) => {
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  return (
    <>
      <Heading size="small" weight="semibold" color="surface.text.gray.normal">
        Bank Account History
      </Heading>
      {accountHistory.map((account) => {
        const badgeProps = getStatusBadgeProps(isRxCurrentAccountMerchant, account);
        return (
          <Accordion key={account.id} marginTop="spacing.6" variant="filled">
            <AccordionItem>
              <AccordionItemHeader>
                <Box display="flex" gap="spacing.3" alignItems="center">
                  <Text color="surface.text.gray.normal" weight="regular">
                    {getActivationPeriod({
                      isActive: account.is_active,
                      createdAt: account.created_at,
                      deactivatedAt: account.deactivated_at,
                    }) || '--'}
                  </Text>
                  {badgeProps?.label ? (
                    <Badge size="small" color={badgeProps.color} emphasis="subtle">
                      {badgeProps.label}
                    </Badge>
                  ) : null}
                </Box>
              </AccordionItemHeader>
              <AccordionItemBody>
                <Box display="flex" marginBottom="spacing.4">
                  <Box display="flex" flexDirection="column" gap="spacing.2" width="50%">
                    <Text size="small" color="surface.text.gray.muted" weight="medium">
                      Account Number
                    </Text>
                    <Text size="medium" color="surface.text.gray.subtle" weight="medium">
                      {account.account_number}
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="small" color="surface.text.gray.muted" weight="medium">
                      IFSC Code
                    </Text>
                    <Text size="medium" color="surface.text.gray.subtle" weight="medium">
                      {account.ifsc}
                    </Text>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap="spacing.2">
                  <Text size="small" color="surface.text.gray.muted" weight="medium">
                    Account Holder Name
                  </Text>
                  <Text
                    truncateAfterLines={1}
                    size="medium"
                    color="surface.text.gray.subtle"
                    weight="medium">
                    {account.beneficiary_name ?? '--'}
                  </Text>
                </Box>
              </AccordionItemBody>
            </AccordionItem>
          </Accordion>
        );
      })}
    </>
  );
};
